import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import SocialButton from '../../components/SocialButton';
import HelpButton from '../../components/HelpButton';
import GameInstructionsModal from '../../components/GameInstructionsModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import getButtonStyle from '../../utils/getButtonStyle';
import { getSessionPlayerNames } from '../../utils/sessionPlayers';
import storageManager from '../../utils/StorageManager';

const categoryOptions = [
    { id: 'movies', label: 'Movies' },
    { id: 'actions', label: 'Actions' },
    { id: 'animals', label: 'Animals' },
    { id: 'random', label: 'Random' },
];

function CharadesSetup() {
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { session, activeSessionContext } = useSessionComms();
    const defaultPlayerName = storageManager.getPlayerName();
    const savedSettings = storageManager.getGameSettings('charades') || {};
    const sessionPlayers = useMemo(
        () => getSessionPlayerNames(session, activeSessionContext?.alias),
        [session, activeSessionContext?.alias]
    );
    const [playerName, setPlayerName] = useState(savedSettings.playerName ?? defaultPlayerName);
    const [players, setPlayers] = useState(savedSettings.players || []);
    const [sessionSeeded, setSessionSeeded] = useState(false);
    const [roundSeconds, setRoundSeconds] = useState(savedSettings.roundSeconds ?? 60);
    const [roundsPerPlayer, setRoundsPerPlayer] = useState(savedSettings.roundsPerPlayer ?? 2);
    const [categories, setCategories] = useState(savedSettings.categories || ['movies', 'actions', 'animals', 'random']);
    const [showInstructions, setShowInstructions] = useState(false);

    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        if (sessionSeeded) return;
        if (players.length > 0) return;
        if (sessionPlayers.length === 0) return;
        setPlayers(sessionPlayers);
        setSessionSeeded(true);
    }, [sessionPlayers, sessionSeeded, players.length]);

    useEffect(() => {
        storageManager.saveGameSettings('charades', {
            playerName: playerName.trim() || defaultPlayerName,
            players,
            roundSeconds,
            roundsPerPlayer,
            categories
        });
    }, [playerName, players, roundSeconds, roundsPerPlayer, categories, defaultPlayerName]);

    useEffect(() => {
        const trimmedName = playerName.trim();
        if (trimmedName) {
            storageManager.savePlayerName(trimmedName);
        }
    }, [playerName]);

    const instructionsSections = [
        {
            heading: 'Objective',
            paragraphs: [
                'Score the most points by getting teammates to guess prompts while you act them out.'
            ]
        },
        {
            heading: 'Round Structure',
            bullets: [
                'Each player gets a timed turn each round.',
                'Tap Start to begin the countdown.',
                'Tap Reveal to show the current prompt to the acting player.',
                'When teammates guess correctly, tap Correct to award a point and load the next prompt.',
                'Tap End Turn when time ends or if you want to stop early.'
            ]
        },
        {
            heading: 'Rules',
            bullets: [
                'No speaking, spelling, or mouthing the exact answer while acting.',
                'Use gestures and body language only.',
                'Keep play fair and avoid giving direct verbal hints.'
            ]
        },
        {
            heading: 'Winning',
            paragraphs: [
                'After all configured rounds are complete, the player/team with the highest score wins.'
            ]
        }
    ];

    const addPlayer = () => {
        const trimmed = playerName.trim();
        if (!trimmed) return;
        setPlayers((prev) => [...prev, trimmed]);
        setPlayerName('');
    };

    const removePlayer = (index) => {
        setPlayers((prev) => prev.filter((_, playerIndex) => playerIndex !== index));
    };

    const toggleCategory = (id) => {
        setCategories((prev) => (
            prev.includes(id)
                ? prev.filter((category) => category !== id)
                : [...prev, id]
        ));
    };

    const startGame = () => {
        const trimmedName = playerName.trim() || defaultPlayerName;
        storageManager.savePlayerName(trimmedName);
        storageManager.saveGameSettings('charades', {
            playerName: trimmedName,
            players,
            roundSeconds,
            roundsPerPlayer,
            categories
        });
        navigate('/charades-game', {
            state: {
                players,
                roundSeconds,
                roundsPerPlayer,
                categories,
            },
        });
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
        }}>
            <BackButton to="/" />
            <HelpButton helpText="Add players, choose categories, and start timed charades rounds." />
            <SettingsButton />
            <SocialButton />

            <div style={{ marginTop: isMobile ? '45px' : '20px', textAlign: 'center' }}>
                <h1 className="page-title">CHARADES SETUP</h1>
                <button
                    onClick={() => setShowInstructions(true)}
                    style={{
                        marginTop: '8px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: theme.accent,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: isMobile ? '0.95rem' : '1rem'
                    }}
                >
                    See full game instructions
                </button>
            </div>

            <div style={{ display: 'grid', gap: '10px', maxWidth: '700px', width: '100%', margin: '0 auto' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <input
                        value={playerName}
                        onChange={(event) => setPlayerName(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && addPlayer()}
                        placeholder="Player name"
                        style={{
                            flex: 1,
                            minWidth: '220px',
                            padding: '12px',
                            borderRadius: '8px',
                            border: `2px solid ${theme.secondary}`,
                            backgroundColor: theme.bgDark,
                            color: theme.textPrimary,
                            fontFamily: 'inherit',
                        }}
                    />
                    <button
                        onClick={addPlayer}
                        style={{
                            ...getButtonStyle(theme, { variant: 'accent', padding: '12px 18px' }),
                        }}
                    >
                        Add
                    </button>
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'grid',
                    gap: '8px'
                }}>
                    <div style={{ fontWeight: 'bold' }}>Players ({players.length})</div>
                    {players.length === 0 ? (
                        <div style={{ color: theme.textSecondary }}>Add at least 2 players.</div>
                    ) : (
                        players.map((player, index) => (
                            <div
                                key={`${player}-${index}`}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: theme.bgMedium,
                                    borderRadius: '8px',
                                    padding: '8px 10px'
                                }}
                            >
                                <span>{index + 1}. {player}</span>
                                <button
                                    onClick={() => removePlayer(index)}
                                    style={{
                                        ...getButtonStyle(theme, { variant: 'danger', padding: '6px 10px', borderRadius: '6px' }),
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: '10px'
                }}>
                    <label style={{ display: 'grid', gap: '6px' }}>
                        <span>Round Time (seconds)</span>
                        <input
                            type="number"
                            min={20}
                            max={180}
                            value={roundSeconds}
                            onChange={(event) => setRoundSeconds(Number(event.target.value) || 60)}
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                border: `2px solid ${theme.secondary}`,
                                backgroundColor: theme.bgDark,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                            }}
                        />
                    </label>

                    <label style={{ display: 'grid', gap: '6px' }}>
                        <span>Rounds per Player</span>
                        <input
                            type="number"
                            min={1}
                            max={6}
                            value={roundsPerPlayer}
                            onChange={(event) => setRoundsPerPlayer(Number(event.target.value) || 2)}
                            style={{
                                padding: '10px',
                                borderRadius: '8px',
                                border: `2px solid ${theme.secondary}`,
                                backgroundColor: theme.bgDark,
                                color: theme.textPrimary,
                                fontFamily: 'inherit',
                            }}
                        />
                    </label>
                </div>

                <div style={{
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '10px',
                    padding: '12px',
                    display: 'grid',
                    gap: '8px'
                }}>
                    <div style={{ fontWeight: 'bold' }}>Prompt Categories</div>
                    {categoryOptions.map((category) => (
                        <label key={category.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                                type="checkbox"
                                checked={categories.includes(category.id)}
                                onChange={() => toggleCategory(category.id)}
                            />
                            <span>{category.label}</span>
                        </label>
                    ))}
                </div>

                <button
                    onClick={startGame}
                    disabled={players.length < 2 || categories.length === 0}
                    style={{
                        ...getButtonStyle(theme, {
                            variant: 'accent',
                            disabled: players.length < 2 || categories.length === 0,
                            padding: '14px 20px',
                            borderRadius: '10px'
                        }),
                    }}
                >
                    Start Charades
                </button>
            </div>

            <GameInstructionsModal
                isOpen={showInstructions}
                onClose={() => setShowInstructions(false)}
                title="Charades - Full Instructions"
                sections={instructionsSections}
                closeLabel="Close Instructions"
            />
        </div>
    );
}

export default CharadesSetup;
